import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../../utils/soundManager';
import './ExplodedView.css';

const ExplodedView = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeComponent, setActiveComponent] = useState(null);
  const sectionRef = useRef(null);

  const components = [
    {
      id: 'crown',
      name: 'Crown',
      description: 'Precision-machined winding crown with triple gasket seal ensuring water resistance and smooth operation.',
      specs: ['Screw-down mechanism', 'Gold-plated', 'Triple sealed']
    },
    {
      id: 'crystal',
      name: 'Sapphire Crystal',
      description: 'Scratch-resistant sapphire crystal with multi-layer anti-reflective coating for perfect clarity.',
      specs: ['Mohs hardness 9', 'AR coated', '2.5mm thick']
    },
    {
      id: 'case',
      name: 'Case',
      description: '18K rose gold case, hand-polished to mirror finish with brushed sides for contrast and elegance.',
      specs: ['18K rose gold', '45mm diameter', 'Mirror polished']
    },
    {
      id: 'movement',
      name: 'Movement',
      description: 'Swiss automatic tourbillon movement with 72-hour power reserve. Hand-decorated with Geneva stripes.',
      specs: ['31 jewels', '28,800 vph', '72h power reserve']
    },
    {
      id: 'dial',
      name: 'Dial',
      description: 'Guilloche-engraved dial with applied gold indices and tourbillon aperture at 6 o\'clock position.',
      specs: ['Guilloche pattern', 'Gold applied markers', 'Luminous hands']
    },
    {
      id: 'caseback',
      name: 'Case Back',
      description: 'Exhibition sapphire case back revealing the beautifully decorated movement and oscillating rotor.',
      specs: ['Sapphire crystal', 'Engraved serial', 'Water sealed']
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleComponentClick = (component) => {
    soundManager.play('ui-click', 0.3);
    setActiveComponent(activeComponent?.id === component.id ? null : component);
  };

  return (
    <section 
      id="craftsmanship" 
      ref={sectionRef}
      className={`exploded-view-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="exploded-container">
        {/* Section Header */}
        <div className="exploded-header">
          <div className="header-label">
            <span className="label-line"></span>
            <span className="label-text">Swiss Craftsmanship</span>
            <span className="label-line"></span>
          </div>
          <h2 className="exploded-title">Engineering Excellence</h2>
          <p className="exploded-subtitle">
            Every CHRONOS timepiece comprises over 200 precision components, 
            each meticulously crafted and assembled by master watchmakers.
          </p>
        </div>

        {/* Main Image with Overlay Info */}
        <div className="exploded-main-content">
          <div className="exploded-image-container">
            <img 
              src="/images/craftsmanship/exploded-view.png" 
              alt="Watch Components Exploded View"
              className="exploded-main-image"
            />
          </div>

          {/* Component Cards Grid */}
          <div className="components-grid">
            {components.map((component, index) => (
              <div
                key={component.id}
                className={`component-card ${activeComponent?.id === component.id ? 'active' : ''}`}
                onClick={() => handleComponentClick(component)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="component-card-header">
                  <span className="component-icon">◆</span>
                  <h3 className="component-name">{component.name}</h3>
                  <span className="component-toggle">
                    {activeComponent?.id === component.id ? '−' : '+'}
                  </span>
                </div>

                {activeComponent?.id === component.id && (
                  <div className="component-details">
                    <p className="component-description">{component.description}</p>
                    <ul className="component-specs">
                      {component.specs.map((spec, idx) => (
                        <li key={idx} className="spec-item">
                          <span className="spec-bullet">•</span>
                          <span className="spec-text">{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="craftsmanship-stats">
          <div className="stat-item">
            <span className="stat-number">200+</span>
            <span className="stat-label">Components</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">400+</span>
            <span className="stat-label">Hours Handwork</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">31</span>
            <span className="stat-label">Jewel Bearings</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">15</span>
            <span className="stat-label">Master Artisans</span>
          </div>
        </div>

        {/* Bottom Image */}
        <div className="craftsmanship-image">
          <img 
            src="/images/craftsmanship/watchmaker-hands.png" 
            alt="Master Watchmaker at Work"
            className="craftsman-image"
          />
          <div className="image-overlay">
            <div className="image-caption">
              <span className="caption-icon">◆</span>
              <p className="caption-text">
                Each timepiece is assembled by a single master watchmaker, 
                ensuring absolute precision and personal accountability.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExplodedView;